import React from 'react';

export interface SkeletonProps {
	className?: string;
	width?: string | number;
	height?: string | number;
	rounded?: boolean;
	animate?: boolean;
}

const Skeleton: React.FC<SkeletonProps> = ({
	className = '',
	width,
	height,
	rounded = false,
	animate = true,
}) => {
	const style: React.CSSProperties = {};

	if (width) {
		style.width = typeof width === 'number' ? `${width}px` : width;
	}

	if (height) {
		style.height = typeof height === 'number' ? `${height}px` : height;
	}

	return (
		<div
			className={`
        bg-gray-200
        ${animate ? 'animate-pulse' : ''}
        ${rounded ? 'rounded-full' : 'rounded'}
        ${className}
      `.trim()}
			style={style}
		/>
	);
};

// Skeleton for text lines
export const SkeletonText: React.FC<{
	lines?: number;
	className?: string;
	lineHeight?: string;
	lastLineWidth?: string;
}> = ({
	lines = 1,
	className = '',
	lineHeight = 'h-4',
	lastLineWidth = 'w-3/4',
}) => {
	return (
		<div className={`space-y-2 ${className}`}>
			{Array.from({ length: lines }).map((_, index) => (
				<Skeleton
					key={index}
					className={`${lineHeight} ${
						index === lines - 1 && lines > 1 ? lastLineWidth : 'w-full'
					}`}
				/>
			))}
		</div>
	);
};

// Skeleton for tables
export const SkeletonTable: React.FC<{
	rows?: number;
	columns?: number;
	showHeader?: boolean;
	className?: string;
}> = ({ rows = 3, columns = 4, showHeader = true, className = '' }) => {
	return (
		<div className={`w-full ${className}`}>
			<table className="w-full min-w-full">
				{/* Table Header */}
				{showHeader && (
					<thead className="bg-gray-50">
						<tr className="border-b border-gray-200">
							{Array.from({ length: columns }).map((_, index) => (
								<th key={`header-${index}`} className="px-6 py-4 text-left">
									<Skeleton className="h-4 w-20" />
								</th>
							))}
						</tr>
					</thead>
				)}

				{/* Table Body */}
				<tbody className="bg-white">
					{Array.from({ length: rows }).map((_, rowIndex) => (
						<tr key={`row-${rowIndex}`} className="border-b border-gray-100">
							{Array.from({ length: columns }).map((_, colIndex) => (
								<td key={`cell-${rowIndex}-${colIndex}`} className="px-6 py-4">
									<Skeleton
										className="h-4"
										width={
											colIndex === 0
												? '90%'
												: colIndex === columns - 1
													? '60%'
													: Math.random() > 0.5
														? '70%'
														: '50%'
										}
									/>
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

// Skeleton for cards
export const SkeletonCard: React.FC<{
	className?: string;
	showTitle?: boolean;
	contentLines?: number;
}> = ({ className = '', showTitle = true, contentLines = 3 }) => {
	return (
		<div
			className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}
		>
			{showTitle && (
				<div className="mb-4">
					<Skeleton className="h-6 w-1/3" />
				</div>
			)}
			<SkeletonText lines={contentLines} />
		</div>
	);
};

// Skeleton for metrics/stats
export const SkeletonMetric: React.FC<{
	className?: string;
	showLabel?: boolean;
}> = ({ className = '', showLabel = true }) => {
	return (
		<div className={`text-center ${className}`}>
			{showLabel && <Skeleton className="h-4 w-20 mx-auto mb-2" />}
			<Skeleton className="h-8 w-24 mx-auto" />
		</div>
	);
};

export default Skeleton;
